import { useEffect } from 'react';
import { useParams } from 'react-router';
import {
  getCandidateDisplayName,
  useGetCandidate,
  type Candidate,
} from '@entities/candidates';
import {
  useGetCandidateVacancies,
  type CandidateProposedVacancy,
} from '@entities/vacancy';
import { CandidateBreadcrumbs } from '@features/candidate/candidateBreadcrumbs';
import { Avatar, Flex, SegmentedControl, Title } from '@mantine/core';
import { getInitials } from '@shared/helpers';
import { useBreadcrumbs } from '@shared/lib';
import { PaginateType } from '@shared/types';
import { EntityMetadata, WithFallback } from '@shared/ui';
import classes from './candidate-info-page.module.scss';
import { GeneralTab } from './components/GeneralTab';
import { ProfilesTab } from './components/Profiles.tab';
import {
  CANDIDATE_INFO_TABS,
  PROPOSED_VACANCIES_PREVIEW_COUNT,
} from './constants';
import { useCandidateInfoTab } from './hooks/useCandidateInfoTab';
import { getEntityMetadata } from './utils/getEntityMetadata';

export type CandidateInfoContentProps = {
  candidate: Candidate;
  proposedVacancies: CandidateProposedVacancy[];
  proposedVacanciesTotal: number;
  isProposedVacanciesLoading: boolean;
};

const CandidateInfoContent = ({
  candidate,
  proposedVacancies,
  proposedVacanciesTotal,
  isProposedVacanciesLoading,
}: CandidateInfoContentProps) => {
  const { activeTab, handleTabChange } = useCandidateInfoTab();
  const photoUrl = candidate.photo?.absolutePath;

  return (
    <Flex wrap='wrap' direction='column' gap={32} mx='auto' maw={1512} w='100%'>
      <EntityMetadata items={getEntityMetadata(candidate)} />

      <Flex align='center' gap={16}>
        <Avatar
          src={photoUrl}
          size={72}
          name={`${candidate.firstName ?? ''} ${candidate.lastName ?? ''}`}
        >
          {!photoUrl &&
            getInitials(
              candidate.firstName ?? undefined,
              candidate.lastName ?? undefined
            )}
        </Avatar>

        <Title order={3} c='primary.7'>
          {getCandidateDisplayName(candidate)}
        </Title>
      </Flex>

      <SegmentedControl
        bg='primary.0'
        aria-label='Разделы информации о кандидате'
        value={activeTab}
        data={CANDIDATE_INFO_TABS}
        onChange={handleTabChange}
        classNames={{ label: classes.label, root: classes.root }}
      />

      {activeTab === 'general' ? (
        <GeneralTab
          candidate={candidate}
          proposedVacancies={proposedVacancies}
          proposedVacanciesTotal={proposedVacanciesTotal}
          isProposedVacanciesLoading={isProposedVacanciesLoading}
        />
      ) : (
        <ProfilesTab candidateId={candidate.id} />
      )}
    </Flex>
  );
};

export const CandidateInfoPage = () => {
  const { id } = useParams();
  const candidateId = id ?? '';
  const { data, isLoading } = useGetCandidate(candidateId);
  const {
    data: proposedVacancies,
    isLoading: isProposedVacanciesLoading,
    isFetching: isProposedVacanciesFetching,
  } = useGetCandidateVacancies(candidateId, {
    filter: {},
    page: 1,
    perPage: PROPOSED_VACANCIES_PREVIEW_COUNT,
    paginate: PaginateType.DEFAULT,
  });
  const { clearBreadcrumb, setBreadcrumb } = useBreadcrumbs();

  useEffect(() => {
    if (!data) {
      return;
    }

    setBreadcrumb(
      <CandidateBreadcrumbs subPage={getCandidateDisplayName(data)} />
    );

    return () => {
      clearBreadcrumb();
    };
  }, [clearBreadcrumb, data, setBreadcrumb]);

  return (
    <WithFallback data={data} isLoading={isLoading}>
      {candidate => (
        <CandidateInfoContent
          candidate={candidate}
          proposedVacancies={proposedVacancies?.data ?? []}
          proposedVacanciesTotal={proposedVacancies?.pagination?.total ?? 0}
          isProposedVacanciesLoading={
            isProposedVacanciesLoading || isProposedVacanciesFetching
          }
        />
      )}
    </WithFallback>
  );
};
